// June 2026 monthly report companion blog.
import { defineDocument, em, link, p, section, strong, text, } from '../../schema';
const juneReportHref = '/party-central/reports/2026-06-top-party-games-and-streamers';
export const blogPartyGameStreamingByTheNumbersJune2026Document = defineDocument({
    id: 'blog:party-game-streaming-by-the-numbers-june-2026',
    kind: 'blog',
    slug: 'party-game-streaming-by-the-numbers-june-2026',
    route: '/blog/party-game-streaming-by-the-numbers-june-2026',
    seo: {
        title: 'Party Game Streaming by the Numbers: June 2026',
        description: "Meccha Chameleon became 2026's fastest-selling game, flooded Twitch in two tracked days, and turned June's party-game data inside out.",
        imageAsset: 'twitchStats202606',
        imageAlt: 'Collage of top party games and streamers from the June 2026 Twitch data report',
    },
    hero: {
        eyebrow: [text('Airhead Blog')],
        title: [text('Party Game Streaming by the Numbers: June 2026')],
        description: [
            text("A self-published Japanese indie has flooded Twitch and become 2026's fastest-selling game."),
        ],
        imageAsset: 'twitchStats202606',
        imageAlt: 'Collage of top party games and streamers from the June 2026 Twitch data report',
        caption: 'Top party games and streamers on Twitch for June 2026, as tracked by the Party Central report.',
        showDescription: true,
        background: 'blurred-image',
    },
    meta: {
        author: 'Jay',
        publishedAt: new Date('2026-07-06T00:00:00.000Z'),
        updatedAt: new Date('2026-07-06T00:00:00.000Z'),
        tags: ['party-central'],
    },
    sections: [
        section({
            id: 'article',
            blocks: [
                p([
                    text('Every monthly report has some games that make a splash. June had a meteor strike. '),
                ]),
                p([
                    em('Meccha Chameleon'),
                    text(' launched in June, sold its first million copies in four days, passed 15 million by the time we wrote the report, and became the fastest and best-selling game of 2026 on word of mouth alone. The Twitch data is just as wild. We only captured '),
                    em('Meccha Chameleon'),
                    text(' in the final two days of June, yet still saw '),
                    strong('10,395 streams'),
                    text(' from '),
                    strong('8,518 streamers'),
                    text(' across '),
                    strong('32 languages'),
                    text('. In those two days, it accounted for more than half of every party game stream we tracked.'),
                ]),
                p([
                    text('The full June 2026 party game streaming report '),
                    link(juneReportHref, 'is now available. '),
                    text('Across the full month, we tracked '),
                    strong('177,900 party game streams'),
                    text(' from nearly '),
                    strong('74,000 streamers'),
                    text(' across '),
                    strong('950 games'),
                    text(' and '),
                    strong('34 languages'),
                    text('; however, the story of June, and possibly the year, is the rapid ascent of a new viral sensation.'),
                ]),
            ],
            sections: [
                section({
                    id: 'article__spotted-at-the-top-of-the-charts',
                    title: 'Spotted at the Top of the Charts',
                    blocks: [
                        p([
                            text(`At its core, `),
                            em('Meccha Chameleon'),
                            text(' is a simple hide-and-seek game: hiders start as plain white characters, choose a place in the map, and paint themselves to blend into the scenery, while seekers walk through the map trying to spot the colorful characters. By combining a universally understood game with mechanics that allow players to express their creativity and humor, the developer, '),
                            link('https://x.com/lemorion1224', 'lemorion1224'),
                            text(', hit on a winning formula that brought it to the top of the charts.'),
                        ]),
                        p([
                            text(`One of the game's strengths is that a viewer can join halfway through, know exactly what's going on, and be hooked into the suspense of the hunt immediately.`),
                            em('Meccha Chameleon'),
                            text(' is also insanely clippable: funny paint jobs, overlooked hiders, and gotcha moments all make for great content on Twitch and social channels. This organic word-of-mouth marketing is what has turned more players and viewers into fans, and launched this game to the top of the charts.'),
                        ]),
                    ],
                }),
                section({
                    id: 'article__cant-hide-from-success',
                    title: `Can't Hide From Success`,
                    blocks: [
                        p([
                            text('The story behind the game makes it easy to root for. '),
                            em('Meccha Chameleon'),
                            text(' is developed and published by an indie developer, with reports describing it as a two-person Japanese project made in about two months. Creator Lemorion and programmer Haganeiro reportedly came from the Fortnite custom map scene and have tried their hand with a few other titles, but this has easily been their biggest success yet.'),
                        ]),
                        p([
                            text('By early July, '),
                            em('Meccha Chameleon'),
                            text(' had sold more than 15 million copies and received more than 26,000 English-language Steam reviews, with 90% positive. Reports also put its player peak around 340,000 concurrent players. A low-priced, self-published party game has turned the industry on its head and challenged the conventional model of game development.'),
                        ]),
                        p([
                            text(`Our Twitch data shows how quick the game's meteoric ascent was. We only started tracking it in the final two days of June, but it already accounted for more than half of every party game stream we tracked. `),
                            em('Meccha Chameleon'),
                            text(' had more than '),
                            strong('10,000 streams'),
                            text(' across nearly every language on Twitch. It became the Most Widely Streamed Game for the entire month, ahead of mainstays like '),
                            em('Among Us'),
                            text(' and '),
                            em('Fall Guys'),
                            text('.'),
                        ]),
                        p([
                            text('In just the final two days of our June tracking data, more than 8,500 creators tried '),
                            em('Meccha Chameleon'),
                            text(`, with the game out-streaming the next nine most popular games combined. While still early in July, we're already seeing the game streamed 12 times more often than the next most popular party game.`),
                        ]),
                    ],
                }),
                section({
                    id: 'article__pinball-wizardry-and-familiar-favorites',
                    title: 'Pinball Wizardry and Familiar Favorites',
                    blocks: [
                        p([
                            text('Beyond the Meccha Chameleon hype, the Party Score leaderboard still had plenty of surprises. '),
                            em('nanos world'),
                            text(' held the #1 spot again this month with its French community continuing to stick with it religiously. The most unusual story was '),
                            em('Pinball'),
                            text(', the repeatable, communal, and surprisingly durable game. It jumped into the #2 spot on the back of a group of dedicated streamers and above-average viewership. This was not a new digital party game catching fire, but rather arcade and venue channels broadcasting physical machines for long stretches.'),
                        ]),
                        p([
                            em('Gartic Phone'),
                            text(', '),
                            em('Mahjong Soul'),
                            text(', and '),
                            em('UNO'),
                            text(' rounded out the top five. '),
                            em('Gartic Phone'),
                            text(' was especially strong in Russian-language streaming. '),
                            em('Mahjong Soul'),
                            text(' made the biggest ranking move, climbing from #10 to #4 as average viewership improved.'),
                        ]),
                        p([
                            em('The House Always Wins'),
                            text(' had the highest average viewership in the top 15 games by Party Score with 215 viewers per stream, but its low stickiness rating points to larger creators dipping in rather than a settled streaming habit.'),
                        ]),
                    ],
                }),
                section({
                    id: 'article__dumbdog-leads-the-pack',
                    title: 'DumbDog Leads the Pack',
                    blocks: [
                        p([
                            link('https://twitch.tv/dumbdog', 'DumbDog'),
                            text(' took the #1 Streamer Party Score by pairing a strong average audience with a real multi-game slate. DumbDog appeared 19 times across seven party games, anchored by '),
                            em('Mahjong Soul'),
                            text(' and lifted by '),
                            em('Among Us'),
                            text(' sessions that averaged more than 1,200 viewers.'),
                        ]),
                        p([
                            link('https://twitch.tv/karacorvus', 'KaraCorvus'),
                            text(' climbed to #2 with six games and a higher average audience than last month, while '),
                            link('https://twitch.tv/pietsmiettv', 'PietSmietTV'),
                            text(' reached #4 and tied for third on the versatility table with 10 games played. '),
                            link('https://twitch.tv/feinekatze', 'FeineKatze'),
                            text(' was one of the more interesting new faces on the Party Score list, playing titles like '),
                            em('Among Us'),
                            text(', '),
                            em('Codenames'),
                            text(', and '),
                            em('Gartic Phone'),
                            text('. '),
                            link('https://twitch.tv/larryfishburger', 'LarryFishburger'),
                            text(' also joined the leaderboard with seven party games played and a higher average audience than last month.'),
                        ]),
                    ],
                }),
                section({
                    id: 'article__read-the-full-report',
                    title: 'Read the Full Report',
                    blocks: [
                        p([
                            text('There is a lot more in the '),
                            link(juneReportHref, 'full June 2026 report'),
                            text(': complete Party Score rankings, new releases, streamer spotlights, language breakdowns, and game deep dives.'),
                        ]),
                    ],
                }),
            ],
        }),
    ],
});
//# sourceMappingURL=party-game-streaming-by-the-numbers-june-2026.js.map